import Swal from "sweetalert2";
import { doDateRangesOverlap, formatDateForConflictMessage, getDateRangeFromRecord, isSameDateRange, parseUploadDateStrict } from "./uploadDateUtils";

/** Canonical metric labels (match connector / report-history esg header). */
export const METRIC = {
  PURCHASED_ELECTRICITY: "Purchased Electricity",
  WITHDRAWAL_WATER: "Withdrawal Water",
  STORED_WATER: "Stored Water",
  RECYCLE_WATER: "Recycle Water",
  DISCHARGE_WATER: "Discharge Water",
  FUEL_STATIONARY: "Fuel consumption (Stationary)",
  FUEL_MOBILE: "Fuel consumption (Mobile)",
  FUGITIVE: "Fugitive Emission",
  PGS: "Purchased goods and services/Capital goods",
  BUSINESS_TRAVEL: "Business Travel",
  UPSTREAM_TRANSPORT: "Upstream Transportation",
  DOWNSTREAM_TRANSPORT: "Downstream Transportation",
  EMPLOYEE_WELLBEING: "Employee Wellbeing",
  WASTE: "Waste Generated in Operations",
};

const norm = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const findField = (record, matcher) => {
  if (!record || typeof record !== "object") return null;
  const key = Object.keys(record).find((k) => matcher(k.toLowerCase()));
  return key != null && record[key] != null && record[key] !== "" ? record[key] : null;
};

const parsePlatformDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  return parseUploadDateStrict(value).date;
};

/**
 * Per-metric date / period policy.
 * mode:
 *   none          — no date validation (Employee Wellbeing)
 *   formatOnly    — parse / order / future only; no overlap
 *   period        — overlap/duplicate on Start–End only
 *   typedPeriod   — overlap/duplicate on typeKey + Start–End
 */
export const getMetricDateValidationPolicy = (esgMetric) => {
  const m = norm(esgMetric);

  if (m === norm(METRIC.EMPLOYEE_WELLBEING)) {
    return { mode: "none", frequencyCheck: false, platformOverwrite: false, label: METRIC.EMPLOYEE_WELLBEING };
  }

  if (m === norm(METRIC.PURCHASED_ELECTRICITY)) {
    return {
      mode: "period",
      frequencyCheck: true,
      platformOverwrite: true,
      label: METRIC.PURCHASED_ELECTRICITY,
      getTypeKey: () => "",
      typeLabel: null,
    };
  }

  const waterPeriodMetrics = [METRIC.WITHDRAWAL_WATER, METRIC.STORED_WATER, METRIC.RECYCLE_WATER, METRIC.DISCHARGE_WATER];
  if (waterPeriodMetrics.some((metric) => m === norm(metric))) {
    return {
      mode: "period",
      frequencyCheck: true,
      platformOverwrite: true,
      label: esgMetric,
      getTypeKey: () => "",
      typeLabel: null,
    };
  }

  // Waste rows carry a reporting month + year, not dates, and the backend row key already makes
  // re-uploads idempotent, so no period/overlap prompting is wanted.
  if (m === norm(METRIC.WASTE)) {
    return { mode: "formatOnly", frequencyCheck: false, platformOverwrite: false, label: METRIC.WASTE, getTypeKey: () => "", typeLabel: null };
  }

  // Fuel, Fugitive, PGS, Business Travel, Transport, Distribution, etc.
  return { mode: "formatOnly", frequencyCheck: false, platformOverwrite: false, label: esgMetric || "Upload" };
};

/** @deprecated use getMetricDateValidationPolicy */
export const isPurchasedElectricityMetric = (esgMetric) => getMetricDateValidationPolicy(esgMetric).label === METRIC.PURCHASED_ELECTRICITY;

export const extractUploadPeriods = (uploadedRows, policy) => {
  const periods = [];
  const getTypeKey = typeof policy?.getTypeKey === "function" ? policy.getTypeKey : () => "";
  (uploadedRows || []).forEach((entry) => {
    const range = getDateRangeFromRecord(entry.data);
    if (range.skipPeriodValidation || range.isSingleDate || !range.start || !range.end) return;
    const typeKey = String(getTypeKey(entry.data) || "");
    const typeDisplay = typeof policy?.getTypeDisplay === "function" ? String(policy.getTypeDisplay(entry.data) || typeKey) : typeKey;
    periods.push({
      rowNumber: entry.rowNumber,
      start: range.start,
      end: range.end,
      typeKey,
      typeDisplay,
    });
  });
  return periods;
};

export const extractPlatformPeriodsFromHistory = (historyPayload, policy) => {
  const records = Array.isArray(historyPayload?.data?.records) ? historyPayload.data.records : Array.isArray(historyPayload?.data) ? historyPayload.data : Array.isArray(historyPayload?.Records) ? historyPayload.Records : [];

  const extractType = policy?.extractTypeFromPlatform;
  const periods = [];
  records.forEach((record, index) => {
    const startRaw = record?.StartDate ?? record?.startDate ?? record?.FromDate ?? record?.fromDate ?? findField(record, (k) => (k.includes("start") || k.includes("from")) && k.includes("date"));
    const endRaw = record?.EndDate ?? record?.endDate ?? record?.ToDate ?? record?.toDate ?? findField(record, (k) => (k.includes("end") || (k.includes("to") && !k.includes("total"))) && k.includes("date"));
    const start = parsePlatformDate(startRaw);
    const end = parsePlatformDate(endRaw);
    if (!start || !end) return;
    const typeDisplay = extractType ? String(extractType(record) || "").trim() : "";
    periods.push({
      index,
      start,
      end,
      typeKey: norm(typeDisplay),
      typeDisplay,
    });
  });
  return periods;
};

const typesMatch = (uploadTypeKey, platformTypeKey, mode) => {
  if (mode === "period") return true;
  if (mode === "typedPeriod") {
    // Same type (including both empty) must conflict; different types allowed.
    return norm(uploadTypeKey) === norm(platformTypeKey);
  }
  return false;
};

export const analyzePlatformPeriodConflicts = (uploadPeriods, platformPeriods, policy) => {
  const exactMatches = [];
  const partialOverlaps = [];
  const seenExact = new Set();
  const seenPartial = new Set();
  const mode = policy?.mode;

  if (mode !== "period" && mode !== "typedPeriod") {
    return { exactMatches, partialOverlaps, hasConflicts: false };
  }

  (uploadPeriods || []).forEach((upload) => {
    (platformPeriods || []).forEach((platform) => {
      if (!typesMatch(upload.typeKey, platform.typeKey, mode)) return;

      const exact = isSameDateRange(upload.start, upload.end, platform.start, platform.end);
      const overlaps = doDateRangesOverlap(upload.start, upload.end, platform.start, platform.end);
      if (!overlaps) return;

      if (exact) {
        const key = `${upload.rowNumber}|${platform.index}|exact`;
        if (!seenExact.has(key)) {
          seenExact.add(key);
          exactMatches.push({ upload, platform });
        }
      } else {
        const key = `${upload.rowNumber}|${platform.index}|partial`;
        if (!seenPartial.has(key)) {
          seenPartial.add(key);
          partialOverlaps.push({ upload, platform });
        }
      }
    });
  });

  return {
    exactMatches,
    partialOverlaps,
    hasConflicts: exactMatches.length > 0 || partialOverlaps.length > 0,
  };
};

export const fetchMetricReportHistory = async (username, siteLocation, esgMetric) => {
  const headers = new Headers();
  headers.append("username", username);
  headers.append("sl", siteLocation);
  headers.append("esg", esgMetric);

  const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}report-history-dynamic`, {
    method: "GET",
    headers,
  });

  if (response.status !== 200) return { records: [], raw: null };
  const json = await response.json();
  if (json.success === false || json.status === 400) return { records: [], raw: json };
  return { raw: json };
};

const formatPeriod = (start, end) => `${formatDateForConflictMessage(start)} – ${formatDateForConflictMessage(end)}`;

const typeSuffix = (period, typeLabel) => {
  if (!typeLabel || !period?.typeDisplay) return "";
  return ` [${typeLabel}: ${period.typeDisplay}]`;
};

const buildConflictLines = (exactMatches, partialOverlaps, typeLabel) => {
  const lines = [];
  partialOverlaps.forEach(({ upload, platform }) => {
    lines.push(`Upload row ${upload.rowNumber} (${formatPeriod(upload.start, upload.end)})${typeSuffix(upload, typeLabel)} ` + `overlaps existing (${formatPeriod(platform.start, platform.end)})${typeSuffix(platform, typeLabel)}.`);
  });
  exactMatches.forEach(({ upload, platform }) => {
    lines.push(`Upload row ${upload.rowNumber} duplicates existing period ${formatPeriod(upload.start, upload.end)}${typeSuffix(upload, typeLabel)}.`);
  });
  return lines;
};

export const confirmPlatformPeriodOverwrite = async ({ exactMatches, partialOverlaps, policy }) => {
  if (!exactMatches?.length && !partialOverlaps?.length) return true;

  const hasPartial = partialOverlaps.length > 0;
  const label = policy?.label || "data";
  const lines = buildConflictLines(exactMatches, partialOverlaps, policy?.typeLabel);
  const listHtml = lines.map((line) => `<p style="font-size:13px; margin: 4px 0; color: #856404;">${line}</p>`).join("");

  const intro = hasPartial ? `The upload overlaps with existing ${label} data. Conflicting existing date ranges will be replaced with the new ranges if you continue.` : `Data for the same date range already exists for ${label}. Do you want to overwrite it?`;

  const result = await Swal.fire({
    icon: "warning",
    html: `<h4 style="color: #f8bb86; font-size: 20px; font-weight: bold;">${hasPartial ? "Overlapping date ranges" : "Duplicate date ranges"}</h4>
      <p style="font-size:16px; margin-bottom: 12px;">${intro}</p>
      <div style="max-height: 280px; overflow-y: auto; text-align: left;">${listHtml}</div>`,
    showCancelButton: true,
    confirmButtonText: "Yes, overwrite",
    cancelButtonText: "No, keep existing",
    width: "680px",
    didOpen: (popup) => {
      const confirmBtn = popup.querySelector(".swal2-confirm");
      const cancelBtn = popup.querySelector(".swal2-cancel");
      if (confirmBtn) {
        confirmBtn.style.minWidth = "160px";
        confirmBtn.style.width = "auto";
        confirmBtn.style.padding = "10px 24px";
        confirmBtn.style.whiteSpace = "nowrap";
      }
      if (cancelBtn) {
        cancelBtn.style.minWidth = "180px";
        cancelBtn.style.width = "auto";
        cancelBtn.style.padding = "10px 24px";
        cancelBtn.style.whiteSpace = "nowrap";
      }
    },
  });

  return result.isConfirmed;
};

/** Back-compat wrappers used by older imports */
export const PURCHASED_ELECTRICITY_ESG = METRIC.PURCHASED_ELECTRICITY;
export const fetchPurchasedElectricityReportHistory = async (username, siteLocation) => {
  const { raw } = await fetchMetricReportHistory(username, siteLocation, METRIC.PURCHASED_ELECTRICITY);
  const policy = getMetricDateValidationPolicy(METRIC.PURCHASED_ELECTRICITY);
  return { records: extractPlatformPeriodsFromHistory(raw, policy), raw };
};
